import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getAssignmentOptions,
  overrideAcknowledgements,
  parseOverrideRefusal,
  updateReservationDetails,
  type AssignmentOptions,
  type ConflictRefusal,
  type Reservation,
  type UpdateReservationDetailsInput,
} from '@/api/reservations';
import { IconAction } from '@/components/IconAction';
import { useResource } from '@/hooks/useResource';
import { Icon, resolveTokens } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import {
  currentIdOf,
  isOffered,
  optionLabel,
} from '@/features/reservations/assignmentOptionsDisplay';
import { focusPicker } from '@/features/reservations/focusPicker';
import { requiredPickerFromError, toBookingErrorMessage } from '@/features/reservations/reservationsError';
import { OverrideConflictDialog } from '@/features/reservations/OverrideConflictDialog';
import { refusedPicker } from '@/features/reservations/refusedPicker';
import { v2ErrorCode } from '@/api/v2Errors';

/** DNKAPI-0636 D-04: the booking changed under this dialog; its seeded fields are stale. */
const STALE_MESSAGE =
  'This booking was changed by someone else and has been reloaded. Close this and open it again.';

interface ChangeAssignmentDialogProps {
  open: boolean;
  reservation: Reservation;
  onClose: () => void;
  /** Success — carries the RETURNED reservation so the caller can patch its row (DNKADM-0257). */
  onDone: (message: string, updated?: Reservation) => void;
  /**
   * DNKAPI-0636 D-04: `409 RESERVATION_CHANGED` — someone else changed the booking first and
   * nothing was written. The host reloads its copy; this dialog blocks Save, because every
   * field it would resend was seeded from the stale row.
   */
  onStale?: () => void;
}

/**
 * Pre-confirmation assignment override. This deliberately stays on PUT `.../details`
 * and sends the current proposed start unchanged; the calendar's confirmed-booking
 * `.../assignment` path is a separate workflow.
 *
 * **Single source of truth for the pickers (DNKADM-0260):** `GET …/{uuid}/assignment-options`
 * — the same slot-aware call the calendar's reassignment dialog makes. It replaced three
 * calls that could not answer the question being asked: `getService` (the modes) plus
 * `listStaff`/`listResources` (the tenant CATALOG, which knows who exists but nothing
 * about this slot), so an admin could pick an already-occupied Bay or coach with no
 * warning. The endpoint returns the modes AND the pools, each option annotated
 * `available` (free over THIS reservation's padded interval, with this reservation
 * self-excluded) and `currentlyAssigned` (the incumbent, preselected). It serves
 * `UNCONFIRMED` reservations — only a terminal row is a `404`.
 *
 * **A conflict is a WARNING in the picker, and — for a coach — a server refusal on
 * save.** An `available:false` option is labelled "(conflict)" and stays fully
 * selectable, unhidden, unreordered, and never disables Save — DNKAPI-0357 dropped the
 * freeness gate on purpose, because a swap where two customers exchange coaches or Bays
 * needs a transient overlap. DNKAPI-0542 then made the coach half of that an explicit
 * choice, and DNKAPI-0549 the rest of it: a pick that would double-book a coach
 * (`STAFF_CONFLICT`), fill a bay past capacity (`RESOURCE_CONFLICT`) or use a coach off
 * shift / past a cap (`STAFF_OUTSIDE_HOURS` / `STAFF_OVER_CAP`) comes back `409`, and
 * **several of those can come back together**. All of them open the
 * {@link OverrideConflictDialog} (DNKADM-0396) with every reason the server named —
 * "Change it" returns here with the chooser the reasons point at focused, "Book anyway"
 * resends the identical body with every acknowledge flag that refusal named, Cancel changes
 * nothing. The picker's "(conflict)" label remains the FORECAST (the `assignment-options`
 * read); the dialog is the refusal. The display rules are shared with the calendar dialog
 * (`reservations/assignmentOptionsDisplay`) so the two cannot drift.
 *
 * **An explicit pick is honored in every assignment mode (DNKAPI-0411, DNKADM-0279).**
 * dink-api's `/details` path applies the admin override at the admin boundary — the
 * same rule as the confirmed-booking reassign — so a specific staff/resource pick
 * takes effect immediately, pre-confirmation, under `MEMBER_SELECTS` AND `ROUND_ROBIN`
 * alike (only a `NONE` dimension rejects an id, 400). The DNKADM-0257 "applies only
 * after confirmation" note this dialog used to render is gone with the limitation it
 * described. On success the RETURNED reservation is passed up so the queue row
 * reflects the server's actual assignment immediately.
 */
export function ChangeAssignmentDialog({
  open,
  reservation,
  onClose,
  onDone,
  onStale,
}: ChangeAssignmentDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${ids}-title`;
  const uuid = reservation.reservationUuid;

  // `null` = untouched, so the effective selection falls back to the loaded current
  // assignment — no effect is needed to seed the preselection once the pools arrive.
  const [staffPick, setStaffPick] = useState<string | null>(null);
  const [resourcePick, setResourcePick] = useState<string | null>(null);
  const [missingPicker, setMissingPicker] = useState<'staff' | 'resource' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [stale, setStale] = useState(false);
  // An admin-override refusal (DNKADM-0396): the server's answer — every reason it named —
  // plus the EXACT body it refused, so "Book anyway" resends that body with the flags that
  // refusal named, not a re-derivation from state.
  const [conflict, setConflict] = useState<{
    view: ConflictRefusal;
    input: UpdateReservationDetailsInput;
  } | null>(null);
  // "Change it" → focus the chooser the server's reasons point at, once the dialog is gone.
  const staffFieldRef = useRef<HTMLDivElement>(null);
  const resourceFieldRef = useRef<HTMLDivElement>(null);
  const [focusRequest, setFocusRequest] = useState<{ n: number; field: 'staff' | 'resource' }>({
    n: 0,
    field: 'staff',
  });
  const options = useResource<AssignmentOptions | null>(
    () => (uuid ? getAssignmentOptions(uuid) : Promise.resolve(null)),
    [uuid],
  );
  // The pickers are disabled while the pools re-read (DNKADM-0437 V-24), and a disabled
  // chooser cannot take focus: wait for the read, and focus each request once.
  const focusedRequestRef = useRef(0);
  useEffect(() => {
    if (focusRequest.n === 0 || focusedRequestRef.current === focusRequest.n) return;
    if (options.status === 'loading') return;
    focusedRequestRef.current = focusRequest.n;
    focusPicker(
      focusRequest.field === 'resource' ? resourceFieldRef.current : staffFieldRef.current,
    );
  }, [focusRequest, options.status]);
  const data = options.data;

  const staffList = data?.staff ?? [];
  const resourceList = data?.resources ?? [];
  const currentStaff = currentIdOf(data?.staff, reservation.assignedStaffId);
  const currentResource = currentIdOf(data?.resources, reservation.assignedResourceId);
  const staffId = staffPick ?? currentStaff;
  const resourceId = resourcePick ?? currentResource;

  const staffOffered = data !== null && isOffered(data.staffMode, staffList);
  const resourceOffered = data !== null && isOffered(data.resourceMode, resourceList);

  const optionsReady = options.status === 'ready' && data !== null;
  const noAssignableDimensions = optionsReady && !staffOffered && !resourceOffered;

  // Double-book warnings — only for a CHANGED pick, since re-selecting the current
  // assignment leaves the booking exactly as it is and must never warn. Mirrors the
  // calendar dialog's summary; the shape of the surrounding state differs, so the
  // logic is mirrored rather than shared.
  const staffChanged = staffOffered && staffId !== '' && staffId !== currentStaff;
  const resourceChanged = resourceOffered && resourceId !== '' && resourceId !== currentResource;
  const conflicts: { key: string; name: string }[] = [];
  const staffConflict = staffChanged ? staffList.find((o) => o.id === staffId) : undefined;
  if (staffConflict && !staffConflict.available) {
    conflicts.push({ key: 'staff', name: staffConflict.name.trim() || 'that staff member' });
  }
  const resourceConflict = resourceChanged
    ? resourceList.find((o) => o.id === resourceId)
    : undefined;
  if (resourceConflict && !resourceConflict.available) {
    conflicts.push({ key: 'resource', name: resourceConflict.name.trim() || 'that resource' });
  }

  function close() {
    if (!submitting) onClose();
  }

  async function save() {
    setError(null);
    setMissingPicker(null);
    if (!uuid) {
      setError("This request can't be edited (missing id).");
      return;
    }
    if (!reservation.start?.trim()) {
      setError("This request can't be edited (missing proposed start).");
      return;
    }
    if (!optionsReady) {
      setError('The assignment options could not be loaded. Try again.');
      return;
    }
    if (noAssignableDimensions) {
      setError('This service has no assignable staff or resource.');
      return;
    }
    if (staffOffered && !staffId) {
      setMissingPicker('staff');
      setError('Choose a staff member for this request.');
      return;
    }
    if (resourceOffered && !resourceId) {
      setMissingPicker('resource');
      setError('Choose a resource for this request.');
      return;
    }

    await send(uuid, {
      start: reservation.start.trim(),
      ...(staffOffered ? { staffId } : {}),
      ...(resourceOffered ? { resourceId } : {}),
    });
  }

  /** The one write path — the first attempt and the acknowledged resend share it. */
  async function send(uuid: string, input: UpdateReservationDetailsInput) {
    setError(null);
    setSubmitting(true);
    try {
      const updated = await updateReservationDetails(uuid, input);
      onDone('Assignment updated.', updated);
    } catch (err) {
      setSubmitting(false);
      const refusal = parseOverrideRefusal(err);
      if (refusal) {
        setConflict({ view: refusal, input });
        return;
      }
      if (v2ErrorCode(err) === 'RESERVATION_CHANGED') {
        // DNKAPI-0636 D-04: a resend would carry the stale row's start/coach/bay and revert
        // the other write, so Save stays off until the dialog is reopened on fresh data.
        setStale(true);
        setError(STALE_MESSAGE);
        onStale?.();
        return;
      }
      const required = requiredPickerFromError(err);
      if (required) setMissingPicker(required);
      setError(toBookingErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}>
          <Icon name="manage_accounts" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Change Resource/Staff</Typography>
          <Typography variant="body2">{scrubBrand(reservation.serviceName ?? 'Reservation')}</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Proposed start: {reservation.start ?? 'Unavailable'}
        </Typography>

        {options.status === 'loading' && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>Loading assignment options...</Typography>
        )}
        {options.status === 'error' && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Assignment options could not be loaded. Try again.
          </Alert>
        )}
        {noAssignableDimensions && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            This service has no assignable staff or resource.
          </Alert>
        )}
        {/* An admin override is allowed — this warns, it does NOT block the save. */}
        {conflicts.map((c) => (
          <Alert key={c.key} severity="warning" sx={{ mt: 1.5 }}>
            This will double-book {scrubBrand(c.name)} at this time.
          </Alert>
        ))}

        <Stack sx={{ gap: 1.5, mt: 1.5 }}>
          {staffOffered && (
            <TextField
              ref={staffFieldRef}
              select
              label="Staff"
              required
              size="small"
              value={staffId}
              onChange={(event) => {
                setStaffPick(event.target.value);
                setMissingPicker(null);
              }}
              error={missingPicker === 'staff'}
              helperText={missingPicker === 'staff' ? 'Choose a staff member.' : ' '}
              disabled={submitting || options.status !== 'ready'}
            >
              {staffList.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {scrubBrand(optionLabel(option))}
                </MenuItem>
              ))}
            </TextField>
          )}
          {resourceOffered && (
            <TextField
              ref={resourceFieldRef}
              select
              label="Resource"
              required
              size="small"
              value={resourceId}
              onChange={(event) => {
                setResourcePick(event.target.value);
                setMissingPicker(null);
              }}
              error={missingPicker === 'resource'}
              helperText={missingPicker === 'resource' ? 'Choose a resource.' : ' '}
              disabled={submitting || options.status !== 'ready'}
            >
              {resourceList.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {scrubBrand(optionLabel(option))}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        {error && <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={
            submitting ||
            stale ||
            !optionsReady ||
            noAssignableDimensions ||
            (staffOffered && !staffId) ||
            (resourceOffered && !resourceId)
          }
        >
          {submitting ? 'Saving...' : 'Save assignment'}
        </Button>
      </DialogActions>

      {/* The admin-override refusal (DNKADM-0396) — a dialog over this one, never a toast.
          Mounted only while a refusal is pending so its acknowledgement tick starts clean
          every time. */}
      {conflict && uuid && (
        <OverrideConflictDialog
          open
          refusal={conflict.view}
          submitting={submitting}
          onCancel={() => setConflict(null)}
          onChangeIt={() => {
            const field = refusedPicker(conflict.view);
            setConflict(null);
            // DNKADM-0437 V-24: the pools were read when the dialog opened; the refusal says
            // availability moved since, so re-read before the operator picks again.
            options.refetch();
            setFocusRequest((prev) => ({ n: prev.n + 1, field }));
          }}
          onBookAnyway={() => {
            const { input, view } = conflict;
            setConflict(null);
            // Exactly the flags THIS refusal named — never one for a decision the admin
            // was not shown.
            void send(uuid, { ...input, ...overrideAcknowledgements(view) });
          }}
        />
      )}
    </Dialog>
  );
}
